<script setup>
import { computed, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { useAlert } from 'dashboard/composables';
import WhatsappConnectionAPI from 'custom/api/whatsappConnection';
import { useLocoI18n } from 'custom/composables/useLocoI18n';

// Moves the inbox to another WhatsApp provider; conversations and contacts stay (same phone-digit ids)
const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useLocoI18n();
const store = useStore();

const PROVIDERS = [
  { value: 'baileys', label: 'LOCO.WHATSAPP.PROVIDERS.BAILEYS', fields: [] },
  {
    value: 'zapi',
    label: 'LOCO.WHATSAPP.PROVIDERS.ZAPI',
    fields: [
      { key: 'instance_id', label: 'LOCO.WHATSAPP.ZAPI_SETUP.INSTANCE_ID' },
      { key: 'token', label: 'LOCO.WHATSAPP.ZAPI_SETUP.TOKEN', secret: true },
      {
        key: 'client_token',
        label: 'LOCO.WHATSAPP.ZAPI_SETUP.CLIENT_TOKEN',
        secret: true,
        optional: true,
      },
    ],
  },
  {
    value: 'whatsapp_cloud',
    label: 'LOCO.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD',
    fields: [
      { key: 'api_key', label: 'LOCO.WHATSAPP.SWITCH.API_KEY', secret: true },
      { key: 'phone_number_id', label: 'LOCO.WHATSAPP.SWITCH.PHONE_NUMBER_ID' },
      {
        key: 'business_account_id',
        label: 'LOCO.WHATSAPP.SWITCH.BUSINESS_ACCOUNT_ID',
      },
    ],
  },
  {
    value: 'default',
    label: 'LOCO.WHATSAPP.PROVIDERS.360DIALOG',
    fields: [
      {
        key: 'api_key',
        label: 'LOCO.WHATSAPP.SWITCH.DIALOG_API_KEY',
        secret: true,
      },
    ],
  },
];

const options = computed(() =>
  PROVIDERS.filter(provider => provider.value !== props.inbox.provider)
);
const selected = ref('');
const config = reactive({});
const isSaving = ref(false);

const fields = computed(
  () =>
    PROVIDERS.find(provider => provider.value === selected.value)?.fields || []
);
const isComplete = computed(
  () =>
    selected.value &&
    fields.value.every(field => field.optional || config[field.key]?.trim())
);

const submit = async () => {
  // eslint-disable-next-line no-alert
  if (!isComplete.value || !window.confirm(t('LOCO.WHATSAPP.SWITCH.CONFIRM')))
    return;

  isSaving.value = true;
  try {
    const providerConfig = Object.fromEntries(
      fields.value
        .filter(field => config[field.key]?.trim())
        .map(field => [field.key, config[field.key].trim()])
    );
    await WhatsappConnectionAPI.switchProvider(
      props.inbox.id,
      selected.value,
      providerConfig
    );
    await store.dispatch('inboxes/get');
    useAlert(t('LOCO.WHATSAPP.SWITCH.SUCCESS'));
    selected.value = '';
  } catch (error) {
    useAlert(
      t('LOCO.WHATSAPP.SWITCH.ERROR', {
        error: error.response?.data?.error || error.message,
      })
    );
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <section class="flex flex-col gap-4">
    <div class="flex flex-col gap-1">
      <h2 class="text-heading-2 text-n-slate-12">
        {{ t('LOCO.WHATSAPP.SWITCH.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('LOCO.WHATSAPP.SWITCH.DESCRIPTION') }}
      </p>
    </div>

    <div class="flex flex-wrap gap-2">
      <Button
        v-for="option in options"
        :key="option.value"
        sm
        :variant="selected === option.value ? 'solid' : 'faded'"
        :color="selected === option.value ? 'blue' : 'slate'"
        :label="t(option.label)"
        @click="selected = option.value"
      />
    </div>

    <form
      v-if="selected"
      class="flex flex-col gap-3 max-w-xl"
      @submit.prevent="submit"
    >
      <Input
        v-for="field in fields"
        :key="`${selected}-${field.key}`"
        v-model="config[field.key]"
        :type="field.secret ? 'password' : 'text'"
        :label="t(field.label)"
      />
      <div>
        <Button
          type="submit"
          ruby
          :label="t('LOCO.WHATSAPP.SWITCH.SUBMIT')"
          :disabled="!isComplete"
          :is-loading="isSaving"
        />
      </div>
    </form>
  </section>
</template>
